import {
  isResultFor,
  type MonthKey,
  matchesWords,
  monthEnd,
  monthStart,
  normalizeArabic,
  type ResultOf,
} from '@horof/shared';
import { useMemo } from 'react';
import { useQuery } from 'urql';
import { graphql } from './gql';

/**
 * The reading round: connected meters in the round's order (`sortOrder`), each
 * with its latest bill up to the selected month. A meter is "read" for the
 * month when that bill falls in the month.
 */
export const ReadingRoundDocument = graphql(`
  query ReadingRound($monthEnd: Date!) {
    meters(
      filter: { meterStatus: { equalTo: CONNECTED } }
      orderBy: [SORT_ORDER_ASC, METER_NO_ASC]
    ) {
      nodes {
        id
        meterNo
        label
        meterAddress
        member {
          id
          fullName
        }
        bills(
          first: 1
          orderBy: [BILL_MONTH_DESC]
          filter: { billMonth: { lessThanOrEqualTo: $monthEnd } }
        ) {
          nodes {
            id
            billMonth
            previousReading
            currentReading
            consumption
            billTotal
          }
        }
        laterBills: bills(filter: { billMonth: { greaterThan: $monthEnd } }) {
          totalCount
        }
      }
    }
  }
`);

type MeterNode = NonNullable<
  NonNullable<ResultOf<typeof ReadingRoundDocument>['meters']>['nodes'][number]
>;
export type RoundBill = NonNullable<MeterNode['bills']['nodes'][number]>;

export type RoundMeter = {
  id: string;
  /** 1-based position in the round. */
  position: number;
  label: string;
  meterNo: string;
  address: string;
  memberName: string;
  /** This month's bill, once the meter is read. */
  bill: RoundBill | null;
  /** The reading this month's reading starts from. */
  previousReading: number;
  /** Month of that previous reading (null for a meter never read). */
  previousMonth: string | null;
  /** Consumption on the previous bill, to spot a mistyped reading. */
  previousConsumption: number | null;
  /** A later month is already billed: this month can no longer be entered. */
  lockedByLaterBill: boolean;
};

export function toRoundMeters(
  nodes: (MeterNode | null)[],
  month: MonthKey,
): RoundMeter[] {
  const start = monthStart(month);
  return nodes.filter(isPresent).map((node, index) => {
    const latest = node.bills.nodes[0] ?? null;
    const bill = latest && latest.billMonth >= start ? latest : null;
    return {
      id: node.id,
      position: index + 1,
      label: node.label ?? node.meterNo,
      meterNo: node.meterNo,
      address: node.meterAddress,
      memberName: node.member?.fullName ?? '',
      bill,
      previousReading: bill
        ? bill.previousReading
        : (latest?.currentReading ?? 0),
      previousMonth: bill ? null : (latest?.billMonth ?? null),
      previousConsumption: bill ? null : (latest?.consumption ?? null),
      lockedByLaterBill: node.laterBills.totalCount > 0,
    };
  });
}

const isPresent = <T>(value: T | null): value is T => value !== null;

/** The next unread meter after `currentId` in round order, wrapping around. */
export function nextUnread(
  meters: RoundMeter[],
  currentId: string,
): RoundMeter | null {
  const from = meters.findIndex((m) => m.id === currentId);
  for (let step = 1; step <= meters.length; step++) {
    const meter = meters[(from + step) % meters.length];
    if (!meter.bill && !meter.lockedByLaterBill && meter.id !== currentId) {
      return meter;
    }
  }
  return null;
}

export function matchesSearch(meter: RoundMeter, query: string): boolean {
  const q = normalizeArabic(query);
  if (/^\d+$/.test(q) && String(meter.position) === q) return true;
  return matchesWords(
    [meter.memberName, meter.label, meter.meterNo, meter.address],
    q,
  );
}

export function useRound(month: MonthKey) {
  const variables = useMemo(() => ({ monthEnd: monthEnd(month) }), [month]);
  const [result, refetch] = useQuery({
    query: ReadingRoundDocument,
    variables,
    // Refetch after a reading is saved, even when no meter had a bill yet.
    context: useMemo(() => ({ additionalTypenames: ['Bill'] }), []),
  });
  // Right after a month change, urql still returns the previous month's
  // result: compute read/unread against *that* month, and say it is switching.
  const current = isResultFor(result.operation, variables);
  const dataMonth = current
    ? month
    : (((
        result.operation?.variables as { monthEnd?: string } | undefined
      )?.monthEnd?.slice(0, 7) as MonthKey | undefined) ?? month);
  const meters = useMemo(
    () => toRoundMeters(result.data?.meters?.nodes ?? [], dataMonth),
    [result.data, dataMonth],
  );
  const readCount = meters.filter((m) => m.bill).length;
  return {
    meters,
    readCount,
    fetching: result.fetching,
    /** The data shown is another month's: the new one is loading. */
    switching: Boolean(result.data) && !current,
    error: result.error,
    loaded: Boolean(result.data),
    refetch,
  };
}
